import React from 'react';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from 'components/ui/table';

const PREVIEW_ROW_COUNT = 5;

const AgenciesStatsTable = ({ stats }) => {
  const preview = stats.slice(0, PREVIEW_ROW_COUNT);
  const max = Math.max(1, ...stats.map((row) => row.soldes));

  if (!stats.length) return <p className="py-8 text-sm text-slate-500">Aucune société pour cette campagne.</p>;

  return (
    <>
      <div className="hidden md:block" data-dashboard-preview="desktop">
        <table className="w-full table-fixed border-collapse text-sm" aria-label="Aperçu de la situation par société">
          <colgroup>
            <col className="w-[13%]" />
            <col className="w-[27%]" />
            <col className="w-[18%]" />
            <col className="w-[14%]" />
            <col className="w-[10%]" />
            <col className="w-[18%]" />
          </colgroup>
          <thead>
            <tr className="text-left text-[13px] font-medium text-emerald-950/75">
              <th colSpan="2" className="pb-3 font-medium">
                Pèlerins soldés
              </th>
              <th colSpan="4" className="pb-3 pl-4 font-medium">
                Acompte, aptitude et passeports
              </th>
            </tr>
            <tr className="border-b text-[13px] text-slate-500">
              <th className="py-3 pr-3 text-left font-normal">Société</th>
              <th aria-label="Pèlerins soldés" />
              <th className="py-3 pl-4 text-left font-normal">Société</th>
              <th className="py-3 text-right font-normal">Acompte payé</th>
              <th className="py-3 text-right font-normal">Aptes</th>
              <th className="py-3 text-right font-normal">Passeports déposés</th>
            </tr>
          </thead>
          <tbody>
            {preview.map((row) => (
              <tr key={row.id} className="h-12 border-b">
                <td className="break-words py-2 pr-3">{row.name}</td>
                <td className="py-2 pr-3">
                  <span className="grid grid-cols-[minmax(0,1fr)_28px] items-center gap-3">
                    <span className="h-1.5 overflow-hidden rounded-sm bg-transparent" aria-hidden="true">
                      <span
                        className="block h-full rounded-sm bg-emerald-600"
                        style={{ width: `${(row.soldes / max) * 100}%` }}
                      />
                    </span>
                    <strong className="text-right text-base text-emerald-800">
                      {row.soldes.toLocaleString('fr-FR')}
                    </strong>
                  </span>
                </td>
                <td className="break-words border-l py-2 pl-4">{row.name}</td>
                <td className="py-2 text-right">{row.deposit_paid.toLocaleString('fr-FR')}</td>
                <td className={`py-2 text-right ${row.medically_fit === 0 ? 'text-slate-400' : ''}`}>
                  {row.medically_fit.toLocaleString('fr-FR')}
                </td>
                <td className={`py-2 text-right ${row.passport_deposited === 0 ? 'text-slate-400' : ''}`}>
                  {row.passport_deposited.toLocaleString('fr-FR')}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <div className="divide-y md:hidden" data-dashboard-preview="mobile">
        {preview.map((row) => (
          <div key={row.id} className="py-4 first:pt-0">
            <p className="font-semibold">{row.name}</p>
            <div className="mt-2 grid grid-cols-[minmax(0,1fr)_auto] items-center gap-3">
              <div className="h-1.5 overflow-hidden rounded-sm bg-slate-100" aria-hidden="true">
                <div className="h-full rounded-sm bg-emerald-600" style={{ width: `${(row.soldes / max) * 100}%` }} />
              </div>
              <span className="text-sm font-semibold text-emerald-800">
                {row.soldes.toLocaleString('fr-FR')} soldés
              </span>
            </div>
            <dl className="mt-3 grid grid-cols-3 gap-3 text-xs">
              {[
                ['Acompte payé', row.deposit_paid],
                ['Aptes', row.medically_fit],
                ['Passeports déposés', row.passport_deposited],
              ].map(([label, value]) => (
                <div key={label}>
                  <dt className="min-h-8 text-slate-500">{label}</dt>
                  <dd className={`mt-1 font-semibold ${value === 0 ? 'text-slate-400' : ''}`}>
                    {value.toLocaleString('fr-FR')}
                  </dd>
                </div>
              ))}
            </dl>
          </div>
        ))}
      </div>

      <details className="mt-4 border-t pt-3">
        <summary className="w-fit cursor-pointer text-sm font-medium text-emerald-700 focus-visible:outline focus-visible:outline-2">
          Voir le tableau complet
        </summary>
        <Table
          containerClassName="mt-3 max-h-[308px] overflow-y-auto"
          containerProps={{
            tabIndex: stats.length > 6 ? 0 : undefined,
            'aria-label': 'Tableau complet des sociétés',
          }}
        >
          <TableHeader className="sticky top-0 z-10 bg-white">
            <TableRow>
              <TableHead>Société</TableHead>
              <TableHead>Soldés</TableHead>
              <TableHead>Acompte payé</TableHead>
              <TableHead>Aptes</TableHead>
              <TableHead>Passeports déposés</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {stats.map((row) => (
              <TableRow key={row.id}>
                <TableCell>{row.name}</TableCell>
                {['soldes', 'deposit_paid', 'medically_fit', 'passport_deposited'].map((field) => (
                  <TableCell key={field}>{row[field].toLocaleString('fr-FR')}</TableCell>
                ))}
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </details>
    </>
  );
};

export default AgenciesStatsTable;
